import React, { useRef, useState } from 'react';
import type { MusicPlayerComponent } from '../../../types/builder';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface MusicPlayerRendererProps {
  component: MusicPlayerComponent;
}

export const MusicPlayerRenderer: React.FC<MusicPlayerRendererProps> = ({ component }) => {
  const { settings } = component;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const style = settings.style || 'default';

  const togglePlay = () => {
    if (!audioRef.current || !settings.audioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Default Style (Original)
  if (style === 'default') {
    return (
      <div
        style={{
          background: settings.backgroundColor,
          color: settings.textColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="max-w-md mx-auto">
          {settings.title && (
            <h2 className="text-xl font-bold text-center mb-6">{settings.title}</h2>
          )}

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
            {settings.coverImage && (
              <div className="mb-6">
                <img
                  src={settings.coverImage}
                  alt={settings.songName}
                  className="w-full aspect-square object-cover rounded-xl shadow-lg"
                />
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-lg font-bold mb-1">{settings.songName}</h3>
              <p className="text-sm opacity-70">{settings.artistName}</p>
            </div>

            <div className="flex items-center justify-center gap-6">
              <button
                onClick={toggleMute}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                style={{ color: settings.accentColor }}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>

              <button
                onClick={togglePlay}
                className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
                style={{ backgroundColor: settings.accentColor }}
                disabled={!settings.audioUrl}
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8" fill="white" />
                ) : (
                  <Play className="w-8 h-8 ml-1" fill="white" />
                )}
              </button>

              <div className="w-9 h-9" />
            </div>

            {settings.audioUrl && (
              <audio
                ref={audioRef}
                src={settings.audioUrl}
                loop={settings.loop}
                onEnded={() => setIsPlaying(false)}
              />
            )}

            {!settings.audioUrl && (
              <p className="text-center text-xs opacity-50 mt-4">
                ⚠️ No audio file selected
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Minimal Style
  if (style === 'minimal') {
    return (
      <div
        style={{
          background: settings.backgroundColor,
          color: settings.textColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="max-w-sm mx-auto text-center">
          {settings.title && (
            <h2 className="text-lg font-semibold mb-4">{settings.title}</h2>
          )}

          <div className="mb-4">
            <h3 className="text-base font-bold">{settings.songName}</h3>
            <p className="text-sm opacity-60">{settings.artistName}</p>
          </div>

          <div className="flex items-center justify-center gap-4">
            <button
              onClick={toggleMute}
              className="p-2 rounded-full hover:opacity-70 transition-opacity"
              style={{ color: settings.textColor }}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-105"
              style={{ backgroundColor: settings.accentColor, color: 'white' }}
              disabled={!settings.audioUrl}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" fill="currentColor" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
              )}
            </button>

            <div className="w-8 h-8" />
          </div>

          {settings.audioUrl && (
            <audio
              ref={audioRef}
              src={settings.audioUrl}
              loop={settings.loop}
              onEnded={() => setIsPlaying(false)}
            />
          )}

          {!settings.audioUrl && (
            <p className="text-xs opacity-40 mt-4">No audio</p>
          )}
        </div>
      </div>
    );
  }

  // Compact Style (Horizontal)
  if (style === 'compact') {
    return (
      <div
        style={{
          background: settings.backgroundColor,
          color: settings.textColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="max-w-lg mx-auto">
          {settings.title && (
            <h2 className="text-base font-semibold mb-3">{settings.title}</h2>
          )}

          <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-xl p-4">
            {settings.coverImage && (
              <img
                src={settings.coverImage}
                alt={settings.songName}
                className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
              />
            )}

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold truncate">{settings.songName}</h3>
              <p className="text-xs opacity-60 truncate">{settings.artistName}</p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={toggleMute}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>

              <button
                onClick={togglePlay}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105"
                style={{ backgroundColor: settings.accentColor, color: 'white' }}
                disabled={!settings.audioUrl}
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" fill="currentColor" />
                ) : (
                  <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                )}
              </button>
            </div>
          </div>

          {settings.audioUrl && (
            <audio
              ref={audioRef}
              src={settings.audioUrl}
              loop={settings.loop}
              onEnded={() => setIsPlaying(false)}
            />
          )}

          {!settings.audioUrl && (
            <p className="text-xs opacity-40 mt-2 text-center">No audio file</p>
          )}
        </div>
      </div>
    );
  }

  // Modern Style (with animation)
  if (style === 'modern') {
    return (
      <div
        style={{
          background: settings.backgroundColor,
          color: settings.textColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="max-w-md mx-auto">
          {settings.title && (
            <h2 className="text-xl font-bold text-center mb-6">{settings.title}</h2>
          )}

          <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 shadow-2xl overflow-hidden">
            {/* Animated background */}
            {isPlaying && (
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-pulse" />
              </div>
            )}

            {settings.coverImage && (
              <div className="relative mb-6">
                <img
                  src={settings.coverImage}
                  alt={settings.songName}
                  className={`w-full aspect-square object-cover rounded-2xl shadow-2xl transition-transform duration-300 ${
                    isPlaying ? 'scale-105' : 'scale-100'
                  }`}
                />
                {isPlaying && (
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/50 to-transparent" />
                )}
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">{settings.songName}</h3>
              <p className="text-sm opacity-70">{settings.artistName}</p>
            </div>

            {/* Wave animation bars (visual only) */}
            {isPlaying && (
              <div className="flex items-center justify-center gap-1 mb-6 h-8">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 rounded-full animate-pulse"
                    style={{
                      backgroundColor: settings.accentColor,
                      height: `${20 + Math.random() * 40}%`,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  />
                ))}
              </div>
            )}

            <div className="flex items-center justify-center gap-6">
              <button
                onClick={toggleMute}
                className="p-3 rounded-full hover:bg-white/10 transition-all"
                style={{ color: settings.accentColor }}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>

              <button
                onClick={togglePlay}
                className="w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-all hover:scale-110 relative overflow-hidden"
                style={{ backgroundColor: settings.accentColor }}
                disabled={!settings.audioUrl}
              >
                {isPlaying && (
                  <div className="absolute inset-0 bg-white/20 animate-ping" />
                )}
                {isPlaying ? (
                  <Pause className="w-10 h-10 relative z-10" fill="white" />
                ) : (
                  <Play className="w-10 h-10 ml-1 relative z-10" fill="white" />
                )}
              </button>

              <div className="w-11 h-11" />
            </div>

            {settings.audioUrl && (
              <audio
                ref={audioRef}
                src={settings.audioUrl}
                loop={settings.loop}
                onEnded={() => setIsPlaying(false)}
              />
            )}

            {!settings.audioUrl && (
              <p className="text-center text-xs opacity-50 mt-4">
                ⚠️ No audio file selected
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};
